import React, { useState } from "react";
import { Box, Button, Card, CircularProgress, Grid, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EventNoteIcon from "@mui/icons-material/EventNote";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import { useNavigate } from "react-router-dom";
import {
  useGetTodayAbaSessionsQuery,
  useCancelTodaySessionMutation,
  useEndProgramStudentAssignmentMutation,
} from "../../../Api/attoDeskApi";
import { LegacyTokens, TypeScale, B13, B13Ink, tint12 } from "../../../theme/b13Tokens";
import { AbaSessionStatusChip, StudentAvatar } from "./SessionBadges";
import { useConfirm } from "../../../Components/Confirm";
import { useNotifier } from "../../../Core/Notifier";

/**
 * Today's Assigned ABA Sessions — premium clinical appointment cards.
 *
 * Cards read from real session_data_records for today unioned with active
 * program_student_assignments for the logged-in staff. No fake schedule.
 *
 * Card kebab (added 2026-08-09) offers two precise write actions:
 *   • Mark as not held today — writes a status='cancelled' session row,
 *     answerable to "how many sessions did not happen this month".
 *   • Discontinue this program for {student} — ends the program-student
 *     assignment (per-student), distinct from the program-level
 *     Discontinued status which kills the program for everyone.
 */
export default function TodaysAbaSessions() {
  const navigate = useNavigate();
  const { data, isFetching } = useGetTodayAbaSessionsQuery({});
  const cards: any[] = Array.isArray(data?.data) ? (data!.data as any[]) : [];

  const start = (card: any) => {
    if (card.sessionId) return navigate(`/aba-therapy/sessions/${card.sessionId}/edit`);
    const q = new URLSearchParams({
      studentId: String(card.studentId ?? ""),
      programId: String(card.programId ?? ""),
      staffId:   String(card.staffId ?? ""),
    });
    navigate(`/aba-therapy/sessions/new?${q.toString()}`);
  };

  return (
    <Card variant="outlined" data-testid="todays-aba-sessions" sx={{
      mb: 3, p: 2.5,
      backgroundColor: LegacyTokens.surface,
      borderColor: LegacyTokens.line,
      borderRadius: `${LegacyTokens.radius.card}px`,
      boxShadow: "none",
    }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }} sx={{ mb: cards.length || isFetching ? 2 : 0 }}>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", minWidth: 0 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: `${LegacyTokens.radius.control}px`,
            backgroundColor: LegacyTokens.tealSoft, color: LegacyTokens.teal,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <EventNoteIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ ...TypeScale.sectionHead, fontSize: 16 }}>
              Today's Assigned ABA Sessions
            </Typography>
            <Typography sx={{ ...TypeScale.caption }}>
              Start scheduled ABA Sessions or continue drafts for today's caseload.
            </Typography>
          </Box>
        </Box>
        <Button
          size="medium"
          startIcon={<EventNoteIcon fontSize="small" />}
          onClick={() => navigate("/aba-therapy/sessions/new")}
          sx={{
            backgroundColor: LegacyTokens.surface, color: B13.primary,
            border: `1px solid ${tint12(B13.primary)}`,
            fontWeight: 600, textTransform: "none",
            px: 2, py: 1, borderRadius: `${LegacyTokens.radius.control}px`,
            flexShrink: 0,
            "&:hover": { backgroundColor: tint12(B13.primary), borderColor: B13.primary },
          }}
        >
          Record Unscheduled Session
        </Button>
      </Stack>

      {isFetching ? (
        <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress size={22} /></Box>
      ) : cards.length === 0 ? (
        <Box sx={{
          mt: 1, p: 3, borderRadius: `${LegacyTokens.radius.control}px`,
          backgroundColor: LegacyTokens.canvas, textAlign: "center",
        }}>
          <Box sx={{
            display: "inline-flex", width: 44, height: 44, borderRadius: `${LegacyTokens.radius.control}px`,
            backgroundColor: LegacyTokens.tealSoft, color: LegacyTokens.teal,
            alignItems: "center", justifyContent: "center", mb: 1.5,
          }}>
            <EventNoteIcon />
          </Box>
          <Typography sx={{ ...TypeScale.sectionHead, mb: 0.5 }}>No ABA Sessions scheduled today</Typography>
          <Typography sx={{ ...TypeScale.caption, maxWidth: 460, mx: "auto" }}>
            Use "Record Unscheduled Session" to record an approved Session outside the regular timetable.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {cards.map((c, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <TodayCard card={c} onStart={() => start(c)} onViewStudent={() => window.open(`/students/${c.studentId}/dashboard`, "_blank")} />
            </Grid>
          ))}
        </Grid>
      )}
    </Card>
  );
}

// Batch 39 — one clinical appointment card. Left accent bar in teal so the
// section reads as coherent. Same actions, same routes, same data.
const TodayCard: React.FC<{ card: any; onStart: () => void; onViewStudent: () => void }> = ({ card, onStart, onViewStudent }) => {
  const isDraft = card.kind === "session";
  // Backend now sends gradeLabel via a `grades.grade` JOIN — was
  // rendering the raw FK (e.g. "Grade 15" instead of "Grade SK").
  // Fall back to the id if the join was empty, so a legitimate row
  // with a grade_id but no grades match doesn't regress to blank.
  const grade = card.gradeLabel
    ? `Grade ${card.gradeLabel}`
    : card.gradeId ? `Grade ${card.gradeId}` : null;
  const metaChips: string[] = [
    ...(card.location ? [card.location] : []),
    ...(card.activeTargets != null ? [`${card.activeTargets} Target${card.activeTargets === 1 ? "" : "s"}`] : []),
    ...(card.studyMode ? [card.studyMode] : []),
  ];

  // Kebab state + write mutations.
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [cancelSession, { isLoading: cancelling }] = useCancelTodaySessionMutation();
  const [endAssignment, { isLoading: ending }] = useEndProgramStudentAssignmentMutation();
  const confirm = useConfirm();
  const { showMessage, showErrorMessage } = useNotifier();

  const handleMarkNotHeld = async () => {
    setMenuAnchor(null);
    try {
      const res = await cancelSession({
        studentId: card.studentId,
        programId: card.programId,
        staffId: card.staffId ?? null,
      }).unwrap();
      if (res.status) {
        showMessage(res.message || "Session marked not-held for today.");
      } else {
        showErrorMessage(res.message || "Failed to mark not-held.");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to mark not-held.");
    }
  };

  const handleDiscontinue = async () => {
    setMenuAnchor(null);
    const studentLabel = card.studentName || "this student";
    const programLabel = card.programName || "This program";
    const ok = await confirm({
      title: `Discontinue program for ${studentLabel}?`,
      message: `${programLabel} will stop appearing in ${studentLabel}'s daily sessions. Their recorded session history is kept. Other students on this program are unaffected.`,
      confirmText: "Discontinue",
      cancelText: "Keep assignment",
      variant: "danger",
    });
    if (!ok) return;
    // The card carries programId + studentId but not the assignment id.
    // The backend maps (programId, studentId, active) → the one row via
    // the unique index psa_unique_active in a follow-up if we surface
    // assignment id on the card payload. For now: the card kind='session'
    // won't have an assignmentId either — we need it in the payload. If
    // absent, ask the backend to look it up. Guarded here so the button
    // never fails silently.
    const assignmentId = card.assignmentId ?? null;
    if (!assignmentId) {
      showErrorMessage("Cannot discontinue: assignment id is missing from the card payload.");
      return;
    }
    try {
      const res = await endAssignment(assignmentId).unwrap();
      if (res.status) {
        showMessage(res.message || "Assignment discontinued.");
      } else {
        showErrorMessage(res.message || "Failed to discontinue assignment.");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to discontinue assignment.");
    }
  };

  return (
    <Card variant="outlined" sx={{
      p: 2, height: "100%",
      backgroundColor: LegacyTokens.surface,
      borderColor: LegacyTokens.line,
      borderRadius: `${LegacyTokens.radius.card}px`,
      borderLeft: `4px solid ${LegacyTokens.teal}`,
      boxShadow: "none",
      display: "flex", flexDirection: "column", gap: 1.5,
      transition: "box-shadow 150ms, transform 150ms",
      "&:hover": { boxShadow: "0 2px 8px rgba(15, 61, 46, 0.08)" },
    }}>
      {/* Top row — avatar, name/grade, status chip, kebab */}
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <StudentAvatar name={card.studentName} />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: LegacyTokens.ink, lineHeight: 1.2 }} noWrap>
            {card.studentName || "Unnamed Student"}
          </Typography>
          {grade && (
            <Box sx={{
              display: "inline-flex", alignItems: "center",
              mt: 0.5,
              backgroundColor: B13.canvas, color: B13Ink.ink3,
              fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
              px: 0.75, py: 0.25, borderRadius: `${LegacyTokens.radius.chip}px`,
            }}>{grade}</Box>
          )}
        </Box>
        <AbaSessionStatusChip status={card.status ?? (isDraft ? "draft" : "in_progress")} />
        <IconButton
          size="small"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          aria-label="Card actions"
          disabled={cancelling || ending}
          sx={{ color: B13Ink.ink3, "&:hover": { color: B13.primary } }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={handleMarkNotHeld} disabled={cancelling}>
            <ListItemIcon><EventBusyOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Mark as not held today" />
          </MenuItem>
          <MenuItem onClick={handleDiscontinue} disabled={ending}>
            <ListItemIcon><LinkOffOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary={`Discontinue this program for ${card.studentName || "student"}`}
            />
          </MenuItem>
        </Menu>
      </Stack>

      {/* Program */}
      <Box>
        <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 0.5 }}>Program</Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: LegacyTokens.ink }} noWrap>
          {card.programName || "Program not set"}
        </Typography>
      </Box>

      {/* Staff */}
      {card.staffName && (
        <Box>
          <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 0.5 }}>Staff</Typography>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <PersonOutlineIcon fontSize="small" sx={{ color: B13Ink.ink3 }} />
            <Typography sx={{ fontSize: 13, color: B13Ink.ink2 }} noWrap>
              {card.staffName}
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Metadata chips */}
      {metaChips.length > 0 && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {metaChips.map((m, i) => (
            <Box key={i} sx={{
              fontSize: 11, color: B13Ink.ink3,
              backgroundColor: B13.canvas,
              px: 1, py: 0.25, borderRadius: `${LegacyTokens.radius.chip}px`,
              fontWeight: 500,
            }}>{m}</Box>
          ))}
        </Stack>
      )}

      {/* Actions */}
      <Stack direction="row" spacing={1} sx={{ mt: "auto", pt: 0.5 }}>
        <Button
          size="medium"
          startIcon={<PlayArrowIcon fontSize="small" />}
          onClick={onStart}
          sx={{
            flexGrow: 1,
            backgroundColor: B13.primary, color: LegacyTokens.surface,
            fontWeight: 600, textTransform: "none",
            py: 0.75, borderRadius: `${LegacyTokens.radius.control}px`,
            boxShadow: "0 1px 2px rgba(15, 61, 46, 0.15)",
            "&:hover": { backgroundColor: B13.primary, boxShadow: "0 2px 6px rgba(15, 61, 46, 0.25)" },
          }}
        >
          {isDraft ? "Continue Draft" : "Start Session"}
        </Button>
        <Button
          size="medium"
          startIcon={<PersonOutlineIcon fontSize="small" />}
          onClick={onViewStudent}
          sx={{
            color: B13.primary, fontWeight: 600, textTransform: "none",
            border: `1px solid ${tint12(B13.primary)}`,
            py: 0.75, borderRadius: `${LegacyTokens.radius.control}px`,
            backgroundColor: LegacyTokens.surface,
            "&:hover": { backgroundColor: tint12(B13.primary), borderColor: B13.primary },
          }}
        >
          View Student
        </Button>
      </Stack>
    </Card>
  );
};
